'use client';

import { Button, Dropdown, DropdownItem, DropdownTitle } from '@ads/components-react';
import { Check, ChevronLeft, ChevronRight } from '@ads/icons';
import { cn } from '@linka/utils';
import type { ComponentPropsWithoutRef } from 'react';

const LANG_DEFAULT_OPTIONS = [
  { value: 'pt', label: 'PT', displayLabel: 'Português - BR', src: 'portuguese.svg' },
  { value: 'en', label: 'EN', displayLabel: 'English - US', src: 'english.svg' },
  { value: 'es', label: 'ES', displayLabel: 'Español - ES', src: 'spanish.svg' },
];

export type Language = 'pt' | 'en' | 'es';

export interface LanguageOption<T extends string> {
  value: T;
  label: string;
  displayLabel?: string;
  src?: string;
}

interface FlagProps<T extends string = Language> {
  language: T;
  options?: LanguageOption<T>[];
}

function Flag<T extends string = Language>({
  language,
  options: optionsProp = LANG_DEFAULT_OPTIONS as LanguageOption<T>[],
}: FlagProps<T>) {
  const selectedIndex = optionsProp.findIndex((langaugeItem) => langaugeItem.value === language);

  return (
    <img
      src={`/svg/${optionsProp[selectedIndex].src}`}
      alt=''
      className='border border-ctx-content-base/semiOpaque rounded-pill'
      width={20}
      height={20}
    />
  );
}

export interface LanguageSelectorTriggerProps<T extends string = Language>
  extends Omit<ComponentPropsWithoutRef<typeof Button>, 'language'> {
  language: T;
  options?: LanguageOption<T>[];
  ref?: React.Ref<HTMLButtonElement>;
}

export function LanguageSelectorTrigger<T extends string = Language>({
  language,
  options: optionsProp = LANG_DEFAULT_OPTIONS as LanguageOption<T>[],
  className,
  ref,
  ...rest
}: LanguageSelectorTriggerProps<T>) {
  const selected = optionsProp.find((option) => option.value === language);

  return (
    <Button
      ref={ref}
      hierarchy='tertiary'
      leadingIcon={() => <Flag language={language} options={optionsProp} />}
      trailingIcon={() => <ChevronRight className='text-ctx-content-base/[0.7] size-mini absolute right-mini' />}
      type='button'
      fullWidth
      className={cn(
        'flex items-center !justify-start gap-nano hover:!bg-[transparent] !px-0 focus:!outline-none focus:!ring-0 focus:!shadow-[none] focus:!bg-[transparent] focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!shadow-[none]',
        className,
      )}
      {...rest}
    >
      <span className='ts-paragraph-xxs text-ctx-content-base/[0.7] text-nowrap'>
        {selected?.displayLabel ?? selected?.label}
      </span>
    </Button>
  );
}

export interface LanguageSelectorPanelProps<T extends string = Language> {
  language: T;
  options?: LanguageOption<T>[];
  onLanguageChange(lang: T): void;
  onBack?: () => void;
  title?: string;
}

export function LanguageSelectorPanel<T extends string = Language>({
  language,
  options: optionsProp = LANG_DEFAULT_OPTIONS as LanguageOption<T>[],
  onLanguageChange,
  onBack,
  title = 'Idioma',
}: LanguageSelectorPanelProps<T>) {
  const options = optionsProp;

  return (
    <>
      <DropdownTitle
        custom={{ content: cn('gap-nano text-ctx-content-title', onBack && 'cursor-pointer') }}
        {...(onBack && { onClick: onBack })}
      >
        {onBack && <ChevronLeft className='size-mini shrink-0' />}
        {title}
      </DropdownTitle>
      {options.map(({ label, displayLabel, value, src }) => {
        const isSelected = value === language;

        return (
          <DropdownItem
            key={value}
            asChild
            onClick={() => onLanguageChange(value)}
            onSelect={(event) => event.preventDefault()}
          >
            <div className='flex w-full items-center'>
              {src && <img src={`/svg/${src}`} alt='' width={16} height={16} />}
              <span
                className={cn(
                  'ml-nano text-xxs',
                  isSelected ? 'text-ctx-content-title' : 'text-ctx-content-base/[0.7]',
                )}
              >
                {displayLabel ?? label}
              </span>
              {isSelected && <Check aria-hidden className='ml-auto size-mini shrink-0 text-ctx-content-title' />}
            </div>
          </DropdownItem>
        );
      })}
    </>
  );
}

interface LanguageSelectorProps<T extends string = Language> {
  language: T;
  options?: LanguageOption<T>[];
  onLanguageChange(lang: T): void;
  title?: string;
}

export function LanguageSelector<T extends string = Language>({
  language,
  options: optionsProp = LANG_DEFAULT_OPTIONS as LanguageOption<T>[],
  onLanguageChange,
  title = 'Idioma',
}: LanguageSelectorProps<T>) {
  return (
    <Dropdown
      align='start'
      className='!w-[19rem]'
      trigger={<LanguageSelectorTrigger language={language} options={optionsProp} />}
    >
      <LanguageSelectorPanel
        language={language}
        options={optionsProp}
        onLanguageChange={onLanguageChange}
        title={title}
        onBack={() => {}}
      />
    </Dropdown>
  );
}
